<template>
  <!-- Standalone progress bar (not in dialog) -->
  <div v-if="!asDialog" class="m-progressbar-wrapper">
    <MStepper v-if="variant === 'steps'" :steps="steps" :orientation="orientation" :label="label"
      :hint="hint" :disabled="disabled" />
    <ProgressBar v-else :value="progressValue" :showValue="false" :class="progressClasses" :style="progressStyle"
      :mode="isIndeterminate ? 'indeterminate' : 'determinate'">
    </ProgressBar>

    <div v-if="hint" class="m-progressbar-hint">
      {{ hint }}
    </div>
  </div>

  <!-- Dialog mode (modal progress bar) -->
  <Dialog v-else v-model:visible="isVisible" :modal="true" :closable="cancelable" :dismissableMask="cancelable"
    :style="{ width: '400px' }" @hide="handleClose">
    <template #header>
      <div class="progress-dialog-header">
        <h3>{{ dialogDescription }}</h3>
      </div>
    </template>

    <div class="progress-dialog-content">
      <ProgressBar :value="progressValue" :showValue="false" :class="progressClasses" :style="progressStyle"
        :mode="isIndeterminate ? 'indeterminate' : 'determinate'">
      </ProgressBar>

      <div v-if="showProgressText && !isIndeterminate" class="progress-dialog-text">
        {{ progressText }}
      </div>
    </div>

    <template #footer v-if="cancelable">
      <Button label="Отмена" icon="pi pi-times" class="p-button-text" @click="handleCancel" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import ProgressBar from 'primevue/progressbar'
import MStepper from '@/components/ui/MStepper.vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { computed, ref, watch, onUnmounted } from 'vue'
import { progressBarService, type ProgressBarInstance } from '@/services/progress/progress.service'

interface Props {
  // Standalone mode props
  modelValue?: number
  max?: number
  label?: string
  hint?: string
  backgroundColor?: string
  showLabel?: boolean
  disabled?: boolean

  // Dialog mode props
  asDialog?: boolean
  instanceId?: string
  description?: string
  cancelable?: boolean
  headlineProgress?: boolean
  variant?: 'bar' | 'steps'
  steps?: unknown
  orientation?: 'horizontal' | 'vertical'
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  max: 100,
  label: '',
  hint: '',
  backgroundColor: undefined,
  showLabel: true,
  disabled: false,
  asDialog: false,
  instanceId: '',
  description: '',
  cancelable: false,
  headlineProgress: true,
  variant: 'bar',
  steps: () => [],
  orientation: 'horizontal'
})

const variant = computed(() => props.variant)
const steps = computed(() => props.steps)
const orientation = computed(() => props.orientation)

const emit = defineEmits<{
  'update:modelValue': [value: number]
  'update:instanceId': [id: string | undefined]
  'cancel': []
  'close': []
}>()

// Standalone mode state
const currentValue = ref(props.modelValue)
const currentMax = ref(props.max)

// Dialog mode state
const isVisible = ref(false)
const instance = ref<ProgressBarInstance | null>(null)
let unsubscribe: (() => void) | null = null

// Computed properties
const isIndeterminate = computed(() => {
  if (props.asDialog && instance.value) {
    return instance.value.max < 0
  }
  return props.max < 0
})

const effectiveMax = computed(() => {
  if (props.asDialog && instance.value) {
    return instance.value.max < 0 ? 100 : instance.value.max
  }
  return props.max < 0 ? 100 : props.max
})

const effectiveCurrent = computed(() => {
  if (props.asDialog && instance.value) {
    return instance.value.current
  }
  return currentValue.value
})

const percentage = computed(() => {
  if (isIndeterminate.value) return undefined
  if (effectiveMax.value === 0) return 0
  return Math.round((effectiveCurrent.value / effectiveMax.value) * 100)
})

const progressValue = computed(() => {
  if (isIndeterminate.value) return 0
  return percentage.value ?? 0
})

const labelText = computed(() => {
  if (props.label) {
    return props.label
  }

  if (props.asDialog && instance.value) {
    if (instance.value.headlineProgress && !isIndeterminate.value) {
      return `${instance.value.current} / ${instance.value.max}`
    }
    if (props.showLabel && percentage.value !== undefined) {
      return `${percentage.value}%`
    }
  }

  return props.showLabel && percentage.value !== undefined ? `${percentage.value}%` : ''
})

const dialogDescription = computed(() => {
  if (props.asDialog && instance.value?.description) {
    return instance.value.description
  }
  return props.description
})

const effectiveHeadlineProgress = computed(() => {
  if (props.asDialog && instance.value) {
    return instance.value.headlineProgress
  }
  return props.headlineProgress
})

const progressText = computed(() => {
  if (props.asDialog && instance.value && effectiveHeadlineProgress.value && !isIndeterminate.value) {
    return `${instance.value.current} / ${instance.value.max}`
  }
  return ''
})

const showProgressText = computed(() => {
  return props.asDialog && effectiveHeadlineProgress.value && !isIndeterminate.value
})

const progressClasses = computed(() => [
  'm-progressbar',
  {
    'm-progressbar--disabled': props.disabled,
    'm-progressbar--indeterminate': isIndeterminate.value
  }
])

const progressStyle = computed(() => {
  const style: Record<string, string> = {}

  if (props.backgroundColor) {
    style['--p-progressbar-value-background'] = props.backgroundColor
  }

  return style
})

// Dialog mode methods
const updateFromInstance = (updatedInstance: ProgressBarInstance) => {
  instance.value = { ...updatedInstance }
  isVisible.value = updatedInstance.visible

  if (updatedInstance.cancelled) {
    isVisible.value = false
  }
}

watch(() => props.instanceId, (newId) => {
  if (newId) {
    // Unsubscribe from previous instance
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }

    // Subscribe to new instance
    unsubscribe = progressBarService.subscribe(newId, updateFromInstance)

    // Get initial state
    const inst = progressBarService.getInstance(newId)
    if (inst) {
      updateFromInstance(inst)
      isVisible.value = inst.visible
    }
  } else {
    isVisible.value = false
  }
}, { immediate: true })

// Watch for standalone mode changes
watch(() => props.modelValue, (newValue) => {
  currentValue.value = newValue
})

watch(() => props.max, (newValue) => {
  currentMax.value = newValue
})

const handleCancel = () => {
  if (props.instanceId) {
    progressBarService.cancel(props.instanceId)
  }
  emit('cancel')
  handleClose()
}

const handleClose = () => {
  isVisible.value = false
  emit('close')
}

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe()
  }
})
</script>

<style scoped lang="scss">
.m-progressbar-wrapper {
  width: 100%;
}

.m-progressbar {
  width: 100%;
  height: var(--p-progressbar-height, 0.5rem);
  overflow: hidden;
  position: relative;
  background: var(--p-progressbar-background, #e5e7eb);
  border-radius: var(--p-progressbar-border-radius, 4px);

  &--disabled {
    opacity: 0.6;
  }

}

.m-progressbar-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}

// Dialog styles
.progress-dialog-header {
  h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
  }
}

.progress-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
}

.progress-dialog-text {
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

</style>
